/**
 * Delivery and engagement, ordered by how urgent each question is.
 *
 * The screen is four layers, and the order is the design:
 *
 *   1. Health   — is anything wrong, and which campaign is causing it
 *   2. Funnel   — where the drop-off is between sending and a click
 *   3. Compare  — the same rates per campaign, side by side
 *   4. Trend    — how all of it moved over the period
 *
 * WHAT THIS REPLACED, AND WHY.
 *
 * The previous version led with seven raw counts and drew five delivery series
 * on one shared linear axis. Measured against ninety days of realistic traffic,
 * that axis was the problem: `sent` peaked at 276 a day while `complained` and
 * `failed` peaked at 1, so two of the five lines were sub-pixel and a third was
 * zero on 74 days of 91. The chart drew five series and communicated two — and
 * the ones it dropped were the ones that pause sending.
 *
 * The counts had the same fault in a different form. "Bounced 102" is not a fact
 * anybody can act on without a denominator, and the denominator was nowhere on
 * the screen.
 *
 * The worst of it was the averaging. A ninety-day aggregate read 2.4% bounce
 * while one campaign inside it sat at 22.9% — the rate that actually suspends an
 * account, hidden by every healthy send around it. No amount of chart work fixes
 * that; it needs the numbers asked for per campaign, which is what
 * `/v1/analytics/breakdown` was added for.
 *
 * TWO THINGS THIS STILL REFUSES TO DO, both carried over deliberately.
 *
 * It never shows a single open rate. Apple Mail Privacy Protection pre-fetches
 * images, so a lone percentage is a number nobody can defend in a meeting. Total
 * and MPP-excluded are always shown together, and the funnel draws the gap.
 *
 * It never stacks delivery outcomes. The nine statuses map to five colours;
 * stacked, the three failure states are one indivisible red band.
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api } from "../api/client";
import { useAsync } from "../hooks";
import { LineChart } from "../components/LineChart";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import { HealthBand } from "../analytics/HealthBand";
import { Funnel } from "../analytics/Funnel";
import { BreakdownTable } from "../analytics/BreakdownTable";
import { FailureStrip } from "../analytics/FailureStrip";
import { formatRate, rate } from "../analytics/rates";
import type {
  AnalyticsBreakdown,
  AnalyticsSeries,
  AnalyticsSummary,
  BreakdownDimension,
  Granularity,
} from "../api/types";

/** The ranges on offer, and the bucket width each implies. */
const RANGES: Array<{ key: string; label: string; days: number; granularity: Granularity }> = [
  { key: "24h", label: "24 hours", days: 1, granularity: "hour" },
  { key: "7d", label: "7 days", days: 7, granularity: "day" },
  { key: "30d", label: "30 days", days: 30, granularity: "day" },
  { key: "90d", label: "90 days", days: 90, granularity: "day" },
];

export function Analytics() {
  const { orgId } = useSession();
  const [params, setParams] = useSearchParams();
  const [dimension, setDimension] = useState<BreakdownDimension>("campaign");

  const rangeKey = params.get("range") ?? "7d";
  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[1]!;

  // Anchored to the minute so a re-render does not produce a new range and
  // refetch on every keystroke elsewhere in the app.
  const { from, to } = useMemo(() => {
    const end = new Date();
    end.setSeconds(0, 0);
    const start = new Date(end.getTime() - range.days * 24 * 60 * 60 * 1000);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [range.days]);

  const summary = useAsync<AnalyticsSummary>(
    (signal) => api.get<AnalyticsSummary>("/v1/analytics/summary", { query: { from, to }, signal }),
    [orgId, from, to],
  );

  const series = useAsync<AnalyticsSeries>(
    (signal) =>
      api.get<AnalyticsSeries>("/v1/analytics/timeseries", {
        query: { from, to, granularity: range.granularity },
        signal,
      }),
    [orgId, from, to, range.granularity],
  );

  const breakdown = useAsync<AnalyticsBreakdown>(
    (signal) =>
      api.get<AnalyticsBreakdown>("/v1/analytics/breakdown", {
        query: { from, to, group_by: dimension, limit: 20 },
        signal,
      }),
    [orgId, from, to, dimension],
  );

  // A request of its own rather than a third option on the toggle above: the
  // campaign table answers "which send", this answers "which provider", and
  // somebody chasing a deliverability problem needs both on screen at once.
  const providers = useAsync<AnalyticsBreakdown>(
    (signal) =>
      api.get<AnalyticsBreakdown>("/v1/analytics/breakdown", {
        query: { from, to, group_by: "mailbox_provider", limit: 20 },
        signal,
      }),
    [orgId, from, to],
  );

  const failed = summary.error ?? series.error;

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Analytics</h1>
        <button
          type="button"
          className="oc-btn oc-btn--ghost oc-btn--sm"
          onClick={() => {
            summary.reload();
            series.reload();
            breakdown.reload();
            providers.reload();
          }}
        >
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="filters">
        <div className="segmented" role="group" aria-label="Period">
          {RANGES.map((option) => (
            <span
              key={option.key}
              className="oc-chip"
              role="button"
              tabIndex={0}
              aria-selected={option.key === range.key}
              onClick={() => setParams({ range: option.key }, { replace: true })}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setParams({ range: option.key }, { replace: true });
                }
              }}
            >
              {option.label}
            </span>
          ))}
        </div>
        <span className="grow" />
        <span className="oc-mono-sm oc-text-muted">bucketed by {range.granularity}</span>
      </div>

      <div className="content">
        {failed && (
          <div className="oc-panel oc-panel--critical" role="alert">
            <div className="oc-panel__body">
              <span className="oc-label">Analytics are unavailable</span>
              <p className="oc-body-sm" style={{ marginBottom: 0 }}>
                {failed.message}
              </p>
            </div>
          </div>
        )}

        {/* 1 · Is anything wrong. */}
        {summary.data && (
          <HealthBand
            totals={summary.data.totals}
            groups={breakdown.data?.group_by === "campaign" ? breakdown.data.groups : undefined}
          />
        )}

        {/* 2 · Where the drop-off is. */}
        {summary.data && (
          <Panel
            title="From send to click"
            caption="Each stage is a share of the one above it, not of the total sent — a message that never arrived is not an engagement failure, and dividing opens by sent understates every campaign in proportion to how badly it bounced."
          >
            <Funnel totals={summary.data.totals} />
          </Panel>
        )}

        {/* 3 · Which send is responsible. */}
        <Panel
          title="By campaign"
          action={
            <div className="segmented" role="group" aria-label="Group by">
              {(["campaign", "template"] as const).map((option) => (
                <span
                  key={option}
                  className="oc-chip"
                  role="button"
                  tabIndex={0}
                  aria-selected={option === dimension}
                  onClick={() => setDimension(option)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setDimension(option);
                    }
                  }}
                >
                  {option === "campaign" ? "Campaign" : "Template"}
                </span>
              ))}
            </div>
          }
          caption="An organization-wide rate averages a bad send away. This is the same period split by what produced it, which is where a failing list actually shows up."
        >
          {breakdown.error ? (
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              {breakdown.error.message}
            </p>
          ) : breakdown.data ? (
            <BreakdownTable groups={breakdown.data.groups} dimension={breakdown.data.group_by} />
          ) : (
            <span className="oc-body-sm oc-text-secondary">Loading…</span>
          )}
        </Panel>

        {/* 3b · Where it is landing. */}
        <Panel
          title="By mailbox provider"
          caption="Each mailbox provider decides on its own whether your mail reaches the inbox, so a block at one of them shows here while the total still looks healthy. Gmail does not report single complaints, so its complaint rate stays at zero here: check Google Postmaster Tools for Gmail. Apple's open rate includes the opens Apple Mail makes for its users. Other includes company domains, whoever filters their mail."
        >
          {providers.error ? (
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              {providers.error.message}
            </p>
          ) : providers.data ? (
            <BreakdownTable groups={providers.data.groups} dimension="mailbox_provider" />
          ) : (
            <span className="oc-body-sm oc-text-secondary">Loading…</span>
          )}
        </Panel>

        {/* 4 · How it moved.
            Three charts rather than two, split by magnitude. See ChartsFor. */}
        {series.data && <ChartsFor series={series.data} />}

        {!failed && !summary.data && (
          <span className="oc-body-sm oc-text-secondary">Loading…</span>
        )}
      </div>
    </>
  );
}

/**
 * The time series, split into three charts by order of magnitude.
 *
 * This is the fix for the axis problem, and it is a layout decision rather than
 * a charting one. Volume, engagement and failures differ by two to three orders
 * of magnitude: putting them on one linear axis does not compress the small
 * series, it erases them. A log axis would keep them visible and make every
 * distance on the chart mean something different at each end, which trades one
 * misreading for a subtler one.
 *
 * So: one axis per magnitude class, each honest within itself. The cost is that
 * a reader cannot compare heights across the three panels, and the caption says
 * so rather than letting somebody assume they can.
 */
function ChartsFor({ series }: { series: AnalyticsSeries }) {
  const labels = series.buckets.map((b) => bucketLabel(b.start, series.granularity));

  const failures = series.buckets.reduce(
    (sum, b) => sum + b.totals.bounced + b.totals.complained + b.totals.failed + b.totals.unsubscribed,
    0,
  );

  return (
    <>
      <Panel
        title="Volume"
        caption="Every bucket in the range, including quiet ones — a line drawn across a gap would imply activity that did not happen."
      >
        <LineChart
          labels={labels}
          ariaLabel={describe(series, "volume")}
          series={[
            { status: "sent", label: "Sent", points: series.buckets.map((b) => b.totals.sent) },
            {
              status: "delivered",
              label: "Delivered",
              points: series.buckets.map((b) => b.totals.delivered),
            },
          ]}
        />
      </Panel>

      <Panel
        title="Engagement"
        caption="Every open and click, so one reader can count more than once: this is activity, and the funnel above counts people. Opens are drawn twice: the total, and — dashed — the total excluding Apple Mail Privacy Protection. The distance between them is how much of the figure is image pre-fetching rather than people."
      >
        <LineChart
          labels={labels}
          ariaLabel={describe(series, "engagement")}
          series={[
            {
              status: "opened",
              label: "Opens",
              points: series.buckets.map((b) => b.totals.opens.total),
            },
            {
              status: "opened",
              label: "Opens excluding MPP",
              dashed: true,
              points: series.buckets.map((b) => b.totals.opens.excluding_mpp),
            },
            {
              status: "clicked",
              label: "Clicks",
              points: series.buckets.map((b) => b.totals.clicked),
            },
          ]}
        />
      </Panel>

      <Panel
        title="Failures"
        caption="Marks, not lines. Each row is scaled to itself, so one complaint is as visible as fifty-five bounces — which is the right emphasis, since a complaint rate pauses sending at a tenth of a percent. Row heights are not comparable with each other; the totals on the right are."
      >
        {failures === 0 ? (
          <div className="oc-empty">
            <span className="oc-empty__title">Nothing failed in this period</span>
            <span className="oc-empty__hint">
              No bounces, complaints, provider failures or unsubscribes.
            </span>
          </div>
        ) : (
          <FailureStrip buckets={series.buckets} granularity={series.granularity} />
        )}
      </Panel>
    </>
  );
}

function Panel({
  title,
  caption,
  action,
  children,
}: {
  title: string;
  caption: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="oc-panel">
      <div className="oc-panel__header">
        <span className="oc-label">{title}</span>
        {action}
      </div>
      <div className="oc-panel__body stack">
        {children}
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          {caption}
        </p>
      </div>
    </div>
  );
}

function bucketLabel(iso: string, granularity: Granularity): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  return granularity === "hour"
    ? at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * What a screen reader is told instead of the picture.
 *
 * An SVG chart is otherwise silent, and the underlying numbers describe the
 * whole period rather than its shape. These are sentences rather than templates
 * with numbers slotted in, because "of which 1 are not attributable" is where
 * that shows.
 */
function describe(series: AnalyticsSeries, which: "volume" | "engagement"): string {
  const t = series.buckets.reduce(
    (sum, b) => ({
      sent: sum.sent + b.totals.sent,
      delivered: sum.delivered + b.totals.delivered,
      opens: sum.opens + b.totals.opens.total,
      real: sum.real + b.totals.opens.excluding_mpp,
      clicks: sum.clicks + b.totals.clicked,
    }),
    { sent: 0, delivered: 0, opens: 0, real: 0, clicks: 0 },
  );

  const over = `over ${series.buckets.length} ${series.granularity} buckets`;

  if (which === "volume") {
    return (
      `Volume ${over}: ${t.sent} sent and ${t.delivered} delivered, ` +
      `a delivery rate of ${formatRate(rate(t.delivered, t.sent))}.`
    );
  }

  return (
    `Engagement ${over}: ${t.opens} opens, of which ${t.real} ` +
    `${t.real === 1 ? "is" : "are"} not attributable to Apple Mail Privacy ` +
    `Protection, and ${t.clicks} clicks.`
  );
}
